import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'query-string';
import OktaWidgetWrapper, {
  isLoginRedirect,
  getWithRedirect,
  hideWidget,
} from '../../components/OktaWidget/okta-widget-wrapper';
import {
  getAppsFromAccessToken,
  setOrRemoveRedirectUrl,
  buildAuth0DaisyChainAuthorizeParams,
  resolveAuth0DaisyChainRetryIdpHint,
  isAuth0DaisyChainReturn,
  getIdTokenLoginHint,
  getOauthBrowsingContextOrigin,
} from '../../okta';
import {
  needsAuth0SessionInit,
  startAuth0SessionInit,
  isInternalUser,
  AUTH0_SESSION_INIT_REDIRECTING,
} from '../../okta/auth0-session-init';
import envConfig from '../../config/config';
import { isEmbeddedInFrame } from '../../utils/ehr-launch-utils';
import 'url-polyfill';
import { ROUTES } from '../../navigation/router';
import { useOktaAuth } from '@okta/okta-react';
import Loader from '@tcl-boron-prefabs/loader';
import logger from '../../utils/logger';
import cn from 'classnames';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../../styles';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';

function SignIn({ classes }) {
  const history = useHistory();
  const { authState } = useOktaAuth();
  const [ehrLogout, setEhrLogout] = React.useState(false);
  const [ehrEmbeddedSessionUnavailable, setEhrEmbeddedSessionUnavailable] = useState(false);
  const daisyChainStartedRef = useRef(false);
  const auth0SessionInitStartedRef = useRef(false);

  const navToAppSwitcher = useCallback(async () => {
    if (authState && authState.isAuthenticated) {
      // Auth0 session init already redirecting: the app switcher's redirect would cancel it mid-flight.
      if (auth0SessionInitStartedRef.current) {
        return;
      }
      // Read the email directly from authState so it's synchronized with isAuthenticated; getTokensSync
      // can transiently return null between the auth state flip and the tokenManager cache being populated.
      const email = authState.idToken?.claims?.email || getIdTokenLoginHint();
      if (needsAuth0SessionInit(email)) {
        auth0SessionInitStartedRef.current = true;
        if ((await startAuth0SessionInit(email)) === AUTH0_SESSION_INIT_REDIRECTING) {
          return;
        }
        // Another tab established the Auth0 session while this one waited: continue to the app switcher.
      }
      const apps = getAppsFromAccessToken(authState.accessToken);
      history.push(ROUTES.APP_SWITCHER, { apps });
    }
  }, [authState, history]);

  const handleFromLoginRedirect = useCallback(async () => {
    // Chain already in flight: the browser is navigating away, so don't start a competing redirect
    // when this effect re-runs as authState settles.
    if (daisyChainStartedRef.current) {
      return;
    }

    const queryObj = qs.parse(window.location.search);

    // SP-initiated and third-party returns carry fromLogin without having reached Auth0, so run the
    // chain even when Okta tokens are already readable — otherwise the user keeps an Okta-only session.
    // Internal users bypass the daisy-chain entirely; their Auth0 session is initialized separately.
    const loginHintEmail = getIdTokenLoginHint();
    const retryIdpHint = isInternalUser(loginHintEmail)
      ? null
      : resolveAuth0DaisyChainRetryIdpHint(queryObj, envConfig);
    if (retryIdpHint) {
      daisyChainStartedRef.current = true;
      hideWidget();
      try {
        // No local email (IdP-initiated SAML): recover the Okta session first so the Auth0 hop
        // can send loginHint. oktaRecovered means we already tried; chain without a hint.
        if (!loginHintEmail && !queryObj.oktaRecovered) {
          const returnParams = new URLSearchParams(window.location.search.replace(/^\?/, ''));
          returnParams.set('fromLogin', 'true');
          returnParams.set('oktaRecovered', '1');
          await getWithRedirect({
            prompt: 'none',
            originalUri: `${getOauthBrowsingContextOrigin()}${window.location.pathname || '/'}?${returnParams}`,
          });
          return;
        }
        await getWithRedirect(buildAuth0DaisyChainAuthorizeParams({ idp: retryIdpHint, loginHint: loginHintEmail }));
        return;
      } catch (err) {
        daisyChainStartedRef.current = false;
        logger.error('Auth0 daisy-chain redirect failed; falling back to standard sign-in', { err });
      }
    }

    // Post-chain return: wait for Okta tokens from the callback — do not call getWithRedirect() while
    // authState is still settling (isAuthenticated false but non-null), or the user gets another login.
    if (isAuth0DaisyChainReturn(queryObj)) {
      if (authState?.isAuthenticated) {
        await navToAppSwitcher();
      }
      return;
    }

    // Already have a readable session (e.g. tokens shared from the EHR popup handoff): finish in place.
    if (authState?.isAuthenticated) {
      await navToAppSwitcher();
      return;
    }

    // Wait for Okta to resolve auth state before deciding, so we don't act on the initial
    // null/undefined state (e.g. before tokens from the EHR popup handoff have been read).
    if (authState == null) {
      return;
    }

    // EHR embedded return with no readable session: the Epic iframe can't see the popup's tokens
    // (partitioned storage). Re-running getWithRedirect here would reopen the popup and loop, so
    // offer to continue in a standalone (first-party) window instead.
    if (queryObj.fromEHR && isEmbeddedInFrame()) {
      logger.warn('EHR embedded return without a readable session; storage is likely partitioned');
      setEhrEmbeddedSessionUnavailable(true);
      return;
    }

    // Standard SSO return: complete the OAuth redirect in this (top-level) window.
    hideWidget();
    await getWithRedirect();
  }, [authState, navToAppSwitcher]);

  const tryRedirecting = useCallback(
    async (queryObj) => {
      const hasFromLoginQueryParam = queryObj?.fromLogin || isLoginRedirect();
      const hasAutoRedirectQueryParam = queryObj?.autoRedirect;
      const hasRedirectQueryParam = queryObj?.redirect;
      const knownButDeferredQueryParams = {
        fromEHR: queryObj?.fromEHR,
        // EHR SSO popup URL (/?ehrSsoStart=true...): handled by OktaWidgetWrapper once mounted, so
        // defer here instead of logging it as an unknown redirect state.
        ehrSsoStart: queryObj?.ehrSsoStart,
      };

      // Set or remove ?redirect= if available
      // Actual redirect handled by AppSwitcher
      setOrRemoveRedirectUrl(queryObj?.redirect);

      // Handle ?fromLogin= if available
      if (hasFromLoginQueryParam) {
        await handleFromLoginRedirect();
      }
      // Handle ?redirect= if available
      else if (hasRedirectQueryParam) {
        await navToAppSwitcher();
      }
      // Handle ?autoRedirect=true if available
      // May not be used anymore
      else if (hasAutoRedirectQueryParam) {
        await getWithRedirect();
      } else if (Object.values(knownButDeferredQueryParams).some((param) => param)) {
        // Known query params that are deferred due to requiring the widget to be mounted
        console.debug('Deferring known query param', { queryObj });
      } else {
        logger.warn('Unknown redirect state', queryObj);
      }
    },
    [handleFromLoginRedirect, navToAppSwitcher],
  );

  useEffect(() => {
    const queryObj = qs.parse(window.location.search);
    // Redirect if query params are present
    if (Object.keys(queryObj).length > 0) {
      tryRedirecting(queryObj);
    } else {
      if (!authState || !authState.isAuthenticated) {
        setOrRemoveRedirectUrl();
      }
    }

    // Only when authenticated, no-op otherwise. Skipped while the daisy chain is redirecting, so an
    // already-authenticated arrival isn't handed to the app switcher before Auth0 is reached.
    if (!resolveAuth0DaisyChainRetryIdpHint(queryObj, envConfig)) {
      navToAppSwitcher();
    }
  }, [authState, tryRedirecting, history, navToAppSwitcher]);

  useEffect(() => {
    async function isEhrLogout() {
      const urlSearchParams = new URLSearchParams(window.location.search);
      if (!!urlSearchParams.get('ehrLogout')) {
        setEhrLogout(true);
        urlSearchParams.delete('ehrLogout');
        history.replace({
          search: urlSearchParams.toString(),
        });
      }
    }
    isEhrLogout();
  }, []); // eslint-disable-line

  if (ehrLogout) {
    return (
      <div className={cn('app-view-container')}>
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            Logged out due to inactivity
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Relaunch the application to log back in.
          </p>
        </>
      </div>
    );
  }

  if (ehrEmbeddedSessionUnavailable) {
    const { redirect } = qs.parse(window.location.search);
    const standaloneUrl = redirect
      ? `${window.location.origin}/?redirect=${encodeURIComponent(redirect)}`
      : `${window.location.origin}/`;
    return (
      <div className={cn('app-view-container')}>
        <>
          <h1 className={cn(typography.H1)} data-testid="ehr-continue-header">
            Continue in a new window
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="ehr-continue-paragraph">
            For your security, we couldn't finish signing you in inside the embedded view. Open your application in a
            separate window to continue.
          </p>
          <p className={cn(typography.Paragraph)}>
            <a
              className={cn(typography.link)}
              href={standaloneUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ehr-continue-link"
            >
              Continue in a new window
            </a>
          </p>
        </>
      </div>
    );
  }

  // Don't mount the email/login widget until we know the user actually needs it. Otherwise it
  // flashes on the OAuth callback return and on an already-authenticated visit, then we bounce to
  // the app switcher.
  const queryObj = qs.parse(window.location.search);
  const inboundSso = Boolean(queryObj.fromLogin || queryObj.autoRedirect || isLoginRedirect());
  if (authState == null || authState.isAuthenticated || inboundSso) {
    return (
      <div className={cn('app-view-container', classes.loadingContainer)} data-testid="signin-loading">
        <Loader />
      </div>
    );
  }

  return <OktaWidgetWrapper />;
}

export default withStyles(styles)(SignIn);
